import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { Reveal } from "@/components/marketing/kit";
import { PLAN_CATALOG, studioNote } from "@/lib/plans";

const PLANS = PLAN_CATALOG.filter(p => p.name !== "Free");
const MATRIX = [
  ["TikTok & Instagram discovery", true, true, true, true],
  ["AI match scoring", true, true, true, true],
  ["AI Creative Studio (AI video & image ads)", true, true, true, true],
  ["Studio credits included / month", "1,000", "2,500", "6,000", "Custom"],
  ["Bonus credits on credit packs", "—", "+10%", "+20%", "+25%"],
  ["AI briefs & outreach", false, true, true, true],
  ["Social listening", false, true, true, true],
  ["EMV & ROI reporting", false, false, true, true],
  ["Multi-client workspaces", false, false, false, true],
  ["API & webhooks", false, false, false, true],
];
const FAQ = [
  ["Can I try HypeFlow before paying?", "Yes. Start free with 10 creator searches a month, campaigns and the creator CRM. Upgrade when you need more volume or the AI Creative Studio."],
  ["How do Studio credits work?", "Every paid plan includes monthly Studio credits (1 credit = $0.10) for AI video, image and voice generation. Unused credits roll over, and you can top up anytime with credit packs. Bigger plans pay less per generation and get bonus credits on packs."],
  ["Is this only for European campaigns?", "No. HypeFlow is built for global brands and agencies. We are based in Amsterdam, but your campaigns can run in any market."],
  ["Can agencies manage several clients?", "Scale and Enterprise are built for higher-volume operations, with dedicated multi-client workspaces and advanced permissions on Enterprise."],
  ["Which platforms are supported?", "We focus on TikTok and Instagram so teams go deeper on the channels where creator-led discovery performs best."],
];

export default function Pricing() {
  const [yearly, setYearly] = useState(false);
  const [discounts, setDiscounts] = useState(null);
  useEffect(() => { api.get("/payments/studio-discounts").then(r => setDiscounts(r.data)).catch(() => {}); }, []);
  const matrix = discounts ? [...MATRIX.slice(0, 4), ["Studio generation pricing", ...["Starter", "Growth", "Scale", "Enterprise"].map(n => studioNote(n, discounts).replace("Studio generations ", "").replace("Standard Studio prices", "Standard"))], ...MATRIX.slice(4)] : MATRIX;
  return (
    <main>
      <section className="mk-sec" style={{ paddingBottom: 20 }}><div className="mk-wrap mk-head">
        <Reveal><span className="mk-pill">Simple, scalable pricing</span></Reveal>
        <Reveal delay={0.08}><h1 className="mk-h2" style={{ fontSize: "clamp(34px,5vw,58px)" }} data-testid="pricing-title">Choose the best plan for your brand</h1></Reveal>
        <Reveal delay={0.16}><p data-testid="pricing-description">Start with the tools you need now. Keep the same workflow as you scale.</p></Reveal>
        <Reveal delay={0.24}><div className="mk-billing" style={{ marginTop: 24 }}>
          <button className={!yearly ? "on" : ""} onClick={() => setYearly(false)} data-testid="billing-monthly">Monthly</button>
          <button className={yearly ? "on" : ""} onClick={() => setYearly(true)} data-testid="billing-yearly">Yearly <span className="save">−20%</span></button>
        </div></Reveal>
      </div></section>

      <section className="mk-sec" style={{ paddingTop: 0 }}><div className="mk-wrap"><div className="mk-plans">
        {PLANS.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.07}><article className={`mk-plan${p.popular ? " pop" : ""}`} data-testid={`public-plan-${p.name.toLowerCase()}`}>
            {p.popular && <span className="mk-plan-tag">★ MOST POPULAR</span>}
            <h3>{p.name}</h3><div className="desc">{p.desc}</div>
            <div className="price" data-testid={`public-plan-price-${p.name.toLowerCase()}`}>
              {p.m === null ? <b>Custom</b> : <><b>${yearly ? p.y : p.m}</b><small>/mo{yearly ? " billed yearly" : ""}</small></>}
            </div>
            <Link to="/login?mode=register" className={`mk-btn block ${p.popular ? "primary" : "ghost"}`} data-testid={`public-plan-select-${p.name.toLowerCase()}`}>{p.name === "Enterprise" ? "Talk to sales" : "Start free"} <ArrowRight size={15} /></Link>
            <ul>{p.features.map(f => <li key={f}><Check /> {f}</li>)}</ul>
          </article></Reveal>
        ))}
      </div></div></section>

      <section className="mk-sec"><div className="mk-wrap">
        <div className="mk-head"><span className="mk-pill">Compare plans</span><h2 className="mk-h2">Every feature, side by side</h2></div>
        <table className="mk-matrix" data-testid="feature-matrix"><thead><tr><th>Feature</th><th>Starter</th><th className="pop-col">Growth</th><th>Scale</th><th>Enterprise</th></tr></thead>
          <tbody>{matrix.map(row => <tr key={row[0]}><td>{row[0]}</td>{row.slice(1).map((v, k) => <td key={k} className={k === 1 ? "pop-col" : ""}>{typeof v === "string" ? v : v ? <Check size={17} /> : "—"}</td>)}</tr>)}</tbody>
        </table>
      </div></section>

      <section className="mk-sec" style={{ paddingTop: 0 }}><div className="mk-wrap">
        <div className="mk-head"><span className="mk-pill">FAQ</span><h2 className="mk-h2">Questions teams ask first</h2></div>
        <div className="mk-faq">{FAQ.map(([q, a], i) => <details key={q} data-testid={`pricing-faq-${i}`}><summary>{q}<span className="pm">+</span></summary><p>{a}</p></details>)}</div>
      </div></section>

      <section className="mk-sec" style={{ paddingTop: 0 }}><div className="mk-wrap"><div className="mk-cta">
        <span className="mk-pill" style={{ margin: "0 auto" }}><Sparkles size={14} /> Start where you are</span>
        <h2>Your next campaign can already run better</h2>
        <div className="mk-cta-actions"><Link to="/login?mode=register" className="mk-btn primary lg" data-testid="pricing-final-trial">Start free <ArrowRight size={17} /></Link><Link to="/contact" className="mk-btn ghost lg">Book a demo</Link></div>
      </div></div></section>
    </main>
  );
}
